import { createRef, KeyboardEvent, useEffect, useMemo } from 'react';
import Select, { StylesConfig } from 'react-select';
import DigitsInput from 'react-verification-input';

import { ArrowDownIcon } from '@/components/icons/main-block';
import { useFilterStore } from '@/store';
import { DropdownOption, DropdownOptions } from '@/types';
import { joinDigits } from '@/utils';

import cn from './styles.module.sass';

const styles: StylesConfig = {
    indicatorSeparator: styles => ({ ...styles, display: 'none' }),
    control: styles => ({
        ...styles,
        border: 'none',
        boxShadow: 'none',
        fontSize: 18,
        lineHeight: '18px',
        cursor: 'pointer',
        backgroundColor: '#E5E7EB',
        fontFamily: 'Azeret Mono',
        maxWidth: 71,
    }),
    menu: styles => ({
        ...styles,
        width: 71,
        borderRadius: 10,
        backgroundColor: '#E5E7EB',
        zIndex: 10,
        top: 30,
        fontFamily: 'Azeret Mono',
    }),
    option: (styles, { isFocused }) => ({
        ...styles,
        backgroundColor: isFocused ? 'white' : '#E5E7EB',
        color: '#111827',
        fontSize: 18,
        cursor: 'pointer',
    }),
};

interface INumberInputProps {
    options: DropdownOptions<number>;
}

const defaultPrefix = {
    value: null,
    label: '9**',
};

export const NumberInput = ({ options }: INumberInputProps) => {
    const optionsWithDefault: DropdownOptions<number | null> = useMemo(
        () => [defaultPrefix, ...options],
        [options]
    );

    const { setSearchParams, prefix, setPrefix, digits, setDigits } = useFilterStore();

    const inputRefs = Array(7)
        .fill(null)
        .map(() => createRef<HTMLInputElement>());

    const handleInputChange = (value: string, key: number) => {
        if (value && key < 6) {
            inputRefs[key + 1].current?.focus();
        }
    };

    const handleKeyDown = (event: KeyboardEvent, index: number) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (event.key === 'Backspace' && !event.target.value && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    const handlePrefixChange = (newValue: unknown) => {
        const prefixValue = newValue as DropdownOption<number>;
        setPrefix(prefixValue);
        setSearchParams({ prefix: prefixValue.value });
    };

    const handleDigitChange = (value: string, key: number) => {
        const newDigits = [...digits];
        newDigits[key] = value;
        handleInputChange(value, key);
        setDigits(newDigits);
    };

    useEffect(() => {
        setSearchParams({ msisdnMask: joinDigits(digits), isFree: false });
    }, [digits]);

    return (
        <div className={cn.numberInputContainer}>
            <div className={cn.operatorNum}>
                <Select
                    components={{
                        DropdownIndicator: () => (
                            <span className={cn.dropdownIcon}>{ArrowDownIcon}</span>
                        ),
                    }}
                    onChange={handlePrefixChange}
                    options={optionsWithDefault}
                    value={prefix}
                    styles={styles}
                    placeholder="9**"
                    isSearchable={false}
                />
            </div>
            <div className={cn.digitsInput}>
                {[...Array(7).keys()].map(key => {
                    return (
                        <DigitsInput
                            key={key}
                            length={1}
                            ref={inputRefs[key]}
                            inputProps={{
                                type: 'tel',
                                onKeyDown: event => handleKeyDown(event, key),
                            }}
                            validChars="0-9"
                            placeholder=""
                            value={digits[key]}
                            onChange={val => handleDigitChange(val, key)}
                            classNames={{
                                character: cn.character,
                                container: cn.container,
                                characterSelected: cn.characterSelected,
                                characterFilled: cn.characterFilled,
                                characterInactive: cn.characterInactive,
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};
